import {
    Grid,
    Tooltip,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Theme,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import tinygradient from "tinygradient";
import * as Yup from 'yup';
import { DayJsDatePicker } from "../../../onboarding/newDatePicker";
import { LoadingButton } from "@mui/lab";

const currentDate = dayjs();

const validationSchema = Yup.object().shape({
    startDate: Yup
        .date()
        .required('From Date is required')
        .typeError('From Date is required')
        .min(new Date("1950-01-01"), "From Date should not be less than 01/01/1950")
        .max(currentDate, "From Date should not be greater than today's date")
        .test('fromDateTest', 'From Date cannot be greater than To Date', function (value) {
            const { endDate } = this.parent;
            return !endDate || !value || value <= endDate;
        }),
    endDate: Yup
        .date()
        .required('To Date is required')
        .typeError('To Date is required')
        .min(new Date("1950-01-01"), "To Date should not be less than 01/01/1950")
        .max(currentDate, "To Date should not be greater than today's date")
        .test('toDateTest', 'To Date cannot be less than From Date', function (value) {
            const { startDate } = this.parent;
            return !startDate || !value || value >= startDate;
        }),
})

const initialValues = {
    startDate: dayjs(currentDate).format("YYYY-MM-DD"),
    endDate: dayjs(currentDate).format("YYYY-MM-DD"),
};


export default function TopHoldingsCorrelation({ data }: { data: (number | string)[][] }): JSX.Element {
    const theme = useTheme()

    const gradient = tinygradient([
        { color: theme.palette.primary[900], pos: 0 },
        { color: theme.palette.primary[200], pos: 0.7 },
        { color: theme.palette.primary[50], pos: 1 }
    ]);
    const colorsRgb = gradient.rgb(7);
    const colorsArr = colorsRgb.map(obj => obj.toHexString())

    const mapValueToColor = (value: number) => {
        if (value >= -0.4 && value < -0.2) {
            return colorsArr[6]
        }
        if (value >= -0.2 && value < 0) {
            return colorsArr[5]
        }
        if (value >= 0 && value < 0.2) {
            return colorsArr[4]
        }
        if (value >= 0.2 && value < 0.4) {
            return colorsArr[3]
        }
        if (value >= 0.4 && value < 0.6) {
            return colorsArr[2]
        }
        if (value >= 0.6 && value < 0.8) {
            return colorsArr[1]
        }
        if (value >= 0.8 && value <= 1) {
            return colorsArr[0]
        }
        return theme.palette.textDark.primary
    }

    const scaleObj: { [key: string]: string } = {
        0: '1.0',
        1: '0.8',
        2: '0.6',
        3: '0.4',
        4: '0.2',
        5: '0',
        6: '-0.2',
        7: '-0.4',
    }


    return (
        <Box
            sx={(theme: Theme) => ({
                borderRadius: "25px",
                padding: "35px",
                backgroundColor: theme.palette.common.white,
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
            })}
        >
            <Typography
                variant="h2"
                sx={(theme: Theme) => ({
                    lineHeight: "25px",
                    letterSpacing: "0.01px",
                    color: theme.palette.text.primary
                })}
            >
                Top Holdings Correlation
            </Typography>
            <Formik
                validationSchema={validationSchema}
                initialValues={initialValues}
                onSubmit={() => console.log("handle submit pressed")}
            >
                {({ values, errors, touched }) => (
                    <Form>
                        <Grid
                            container
                            sx={{
                                marginTop: "32px",
                                gap: "15px",
                                alignItems: "center"
                            }}
                        >
                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <DayJsDatePicker
                                    id=""
                                    name='startDate'
                                    label="From Date"
                                    maxDate={new Date()}
                                    styleObj={{
                                        backgroundColor: theme.palette.primary[50],
                                        borderRadius: "10px",
                                        '&.MuiTextField-root': {
                                            '& fieldset': {
                                                border: `0.5px solid ${theme.palette.primary[50]}`,
                                            },
                                            '.MuiInputBase-root': {
                                                '.MuiInputBase-input': {
                                                    ...theme.typography.body_500,
                                                    letterSpacing: "0.32px",
                                                    color: theme.palette.primary[500],
                                                    padding: "15px"
                                                },
                                            }
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <DayJsDatePicker
                                    id=""
                                    name='endDate'
                                    label="To Date"
                                    minDate={new Date(values.startDate)}
                                    maxDate={new Date()}
                                    styleObj={{
                                        backgroundColor: theme.palette.primary[50],
                                        borderRadius: "10px",
                                        '&.MuiTextField-root': {
                                            '& fieldset': {
                                                border: `0.5px solid ${theme.palette.primary[50]}`,
                                            },
                                            '.MuiInputBase-root': {
                                                '.MuiInputBase-input': {
                                                    ...theme.typography.body_500,
                                                    letterSpacing: "0.32px",
                                                    color: theme.palette.primary[500],
                                                    padding: "15px"
                                                },
                                            }
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid
                                item
                                container
                                xs={12}
                                md={3}
                                sx={{
                                    marginLeft: { xs: "0px", md: "15px" },
                                    flexDirection: "column"
                                }}
                            >
                                <LoadingButton
                                    loadingPosition="start"
                                    type="submit"
                                    variant="contained"
                                    sx={(theme: Theme) => ({
                                        ...theme.typography.body_500,
                                        color: theme.palette.textDark.primary,
                                        backgroundColor: theme.palette.primary[500],
                                        padding: "10px",
                                        fontWeight: "600"
                                    })}
                                    fullWidth
                                >
                                    Proceed
                                </LoadingButton >
                                {(Object.keys(errors).length && Object.keys(touched).length) ? (
                                    <Box sx={{ visibility: "hidden" }}>Hidden</Box>
                                ) : null}
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
            <Box
                sx={{
                    marginTop: "56px",
                    display: "flex",
                    flexWrap: "nowrap",
                    columnGap: "10px",
                }}
            >
                <Box sx={{ flex: 10, width: "100%" }}>
                    <TableContainer sx={{ overflowY: 'hidden' }}>
                        <Table>
                            <TableBody>
                                {data.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        {row.map((cell, cellIndex) => {
                                            const isLastRow = rowIndex === data.length - 1;
                                            const isFirstCell = cellIndex === 0;

                                            const tableCellStyleObj = {
                                                backgroundColor: typeof cell === "number" ? mapValueToColor(cell) : theme.palette.common.white,
                                                height: isLastRow ? "192px" : "72px",
                                                width: "80px",
                                                padding: "0px",
                                                borderBottom: "none",
                                                ...(isFirstCell && {
                                                    width: "400px",
                                                    paddingRight: "20px",
                                                }),
                                                verticalAlign: isLastRow ? "bottom" : "inherit",
                                                textAlign: isLastRow ? "center" : "inherit",
                                            }

                                            const typographyStyleObj = {
                                                ...((isLastRow || isFirstCell) ? theme.typography.subtitle_500 : theme.typography.subtitle_400),
                                                fontWeight: (isLastRow || isFirstCell) ? 500 : 600,
                                                lineHeight: isLastRow ? "17.5px" : "20px",
                                                letterSpacing: "0.02px",
                                                color: typeof cell === "number" ? (
                                                    colorsArr.slice(0, 4).includes(mapValueToColor(cell)) ? theme.palette.common.white : theme.palette.text.primary
                                                ) : theme.palette.text.primary,
                                                textAlign: isFirstCell ? "left" : "center",
                                                ...(isLastRow && {
                                                    writingMode: 'vertical-rl',
                                                    transform: 'rotate(180deg)',
                                                    whiteSpace: 'wrap',
                                                    marginTop: "15px",
                                                    textAlign: "left"
                                                }),
                                            }
                                            return (
                                                <TableCell key={cellIndex} sx={tableCellStyleObj}>
                                                    <Tooltip title={typeof cell === "number" ? cell.toFixed(2) : cell}>
                                                        <Typography component={isLastRow ? "span" : "div"} sx={typographyStyleObj}
                                                        >
                                                            {typeof cell === "number" ? cell.toFixed(2) : cell}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        sx={(theme: Theme) => ({
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            height: 72 * (data.length - 1),
                            position: "relative",
                            width: '30px',
                            background: `linear-gradient(180deg, ${theme.palette.primary[900]} 0%, ${theme.palette.primary[200]} 71.06%, ${theme.palette.primary[50]} 100%)`,
                        })}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                        >
                            {[...Array(7)].map((_, index) => (
                                <Box key={index} sx={{ height: (72 * (data.length - 1)) / 7, position: 'relative' }}>
                                    <Typography
                                        variant="subtitle_500"
                                        sx={{ position: "absolute", right: -30, top: -5 }}
                                    >
                                        {scaleObj[index]}
                                    </Typography>
                                    {index === 6 && (
                                        <Typography
                                            variant="subtitle_500"
                                            sx={{ position: "absolute", right: -30, bottom: -5 }}
                                        >
                                            {scaleObj[index + 1]}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box >
    )
}

